import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import CountdownTimer from '../../components/base/CountdownTimer';
import ProjectsCarousel from '../../components/feature/ProjectsCarousel';
import { createClient } from '@supabase/supabase-js';
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [hasUnreadFeedback, setHasUnreadFeedback] = useState(false);
  const [hasUnreadAudioResponse, setHasUnreadAudioResponse] = useState(false);
  const navigate = useNavigate();

  const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
  );

  useEffect(() => {
    fetchSubmissionCount();
    checkUnreadFeedback();
    checkUnreadAudioResponse();

    // 🔔 Écouter les nouvelles réponses audio en temps réel
    const channel = supabase
      .channel('audio_responses_home')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audio_messages',
          filter: 'message_type=eq.response'
        },
        async (payload) => {
          console.log('🔔 Nouvelle réponse audio détectée sur la page d\'accueil:', payload);
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session && payload.new) {
            const newMessage = payload.new as any;
            
            // Vérifier si c'est une réponse pour cet utilisateur
            if (newMessage.user_id === session.user.id) {
              console.log('✅ Nouvelle réponse audio pour l\'utilisateur connecté !');
              setHasUnreadAudioResponse(true);
              
              // Notification navigateur
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('🎉 Nouvelle réponse audio !', {
                  body: `Vous avez reçu une réponse pour le message ${newMessage.message_number}`,
                  icon: '/favicon.ico',
                  tag: 'audio-response-home',
                  requireInteraction: false
                });
              }
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Statut de la souscription audio (home):', status);
      });

    // Demander la permission pour les notifications
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchSubmissionCount = async () => {
    try {
      const { count, error } = await supabase
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .eq('payment_status', 'completed');

      if (error) {
        console.error('Erreur lors de la récupération du nombre d\'inscriptions:', error);
        return;
      }

      setSubmissionCount(count || 0);
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre d\'inscriptions:', error);
    }
  };

  const checkUnreadFeedback = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        const { data, error } = await supabase
          .from('registrations')
          .select('feedback_viewed, admin_notes, rating')
          .eq('user_id', session.user.id)
          .eq('payment_status', 'completed')
          .single();

        if (!error && data) {
          // Vérifier si l'utilisateur a un feedback ou une note et ne l'a pas encore vu
          const hasFeedback = data.admin_notes || data.rating > 0;
          setHasUnreadFeedback(hasFeedback && !data.feedback_viewed);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du feedback:', error);
    }
  };

  const checkUnreadAudioResponse = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Récupérer tous les messages audio de l'utilisateur
        const { data: messages, error } = await supabase
          .from('audio_messages')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (!error && messages) {
          // Vérifier s'il y a des réponses non lues
          const hasUnreadResponse = messages.some(msg => 
            msg.message_type === 'response' && !msg.viewed_by_user
          );
          setHasUnreadAudioResponse(hasUnreadResponse);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des réponses audio:', error);
    }
  };

  const handleNotificationClick = () => {
    navigate('/projets');
  };

  const handleAudioNotificationClick = async () => {
    // Marquer les réponses comme vues
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        await supabase
          .from('audio_messages')
          .update({ viewed_by_user: true })
          .eq('user_id', session.user.id)
          .eq('message_type', 'response')
          .eq('viewed_by_user', false);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des réponses:', error);
    }
    
    navigate('/chat-vocal');
  };

  const handleParticipateClick = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate('/connexion');
    } else {
      navigate('/registration');
    }
  };

  const progressPercentage = (submissionCount / 1000) * 100;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />

      {/* Notification de feedback */}
      {hasUnreadFeedback && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 100 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="fixed bottom-6 right-6 z-50"
        >
          <button
            onClick={handleNotificationClick}
            className="relative w-16 h-16 bg-gradient-to-br from-accent to-accent-dark rounded-full shadow-2xl hover:shadow-[0_0_30px_rgba(255,107,53,0.6)] transition-all hover:scale-110 cursor-pointer flex items-center justify-center group"
          >
            <i className="ri-notification-3-line text-2xl text-white"></i>
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">1</span>
            </span>
            <div className="absolute bottom-full right-0 mb-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Nouveau feedback disponible !
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </button>
        </motion.div>
      )}

      {/* 🔔 NOUVEAU : Notification de réponse audio */}
      {hasUnreadAudioResponse && !hasUnreadFeedback && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 100 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="fixed bottom-6 right-6 z-50"
        >
          <button
            onClick={handleAudioNotificationClick}
            className="relative w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full shadow-2xl hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all hover:scale-110 cursor-pointer flex items-center justify-center group"
          >
            <i className="ri-mic-line text-2xl text-white"></i>
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">1</span>
            </span>
            <div className="absolute bottom-full right-0 mb-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Nouvelle réponse audio !
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </button>
        </motion.div>
      )}

      {/* 🔔 Si les deux notifications sont présentes, afficher les deux boutons */}
      {hasUnreadFeedback && hasUnreadAudioResponse && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 100 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="fixed bottom-6 right-24 z-50"
        >
          <button
            onClick={handleAudioNotificationClick}
            className="relative w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full shadow-2xl hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all hover:scale-110 cursor-pointer flex items-center justify-center group"
          >
            <i className="ri-mic-line text-2xl text-white"></i>
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">1</span>
            </span>
            <div className="absolute bottom-full right-0 mb-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Nouvelle réponse audio !
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </button>
        </motion.div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 pt-20 sm:pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-secondary-light"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/15"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/1534c1b2e71d45dec95aa67fae9455a9.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>

        <div className="relative z-10 max-w-5xl mx-auto text-center px-4 sm:px-6 py-12 sm:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/25 backdrop-blur-sm rounded-full border border-white/30 mb-6 sm:mb-8 shadow-lg"
          >
            <i className="ri-trophy-line text-white text-sm sm:text-base"></i>
            <span className="text-white text-xs sm:text-sm font-medium">Première Édition 2026</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight px-2 drop-shadow-lg"
          >
            Votre projet mérite de voir le <span className="text-accent drop-shadow-[0_2px_8px_rgba(255,107,53,0.8)]">jour</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl text-white/95 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-2 drop-shadow-md"
          >
            Jusqu'à 10 000€ à gagner, mentorat sur mesure et feedback garanti.<br className="hidden sm:block" />
            Première édition limitée à 1000 participants.
          </motion.p>

          {/* Countdown Timer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 sm:mb-10"
          >
            <CountdownTimer />
          </motion.div>

          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="max-w-2xl mx-auto mb-8 sm:mb-10"
          >
            <div className="bg-white/15 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/25 shadow-xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-semibold text-sm sm:text-base drop-shadow-md">
                  <i className="ri-user-line mr-2"></i>
                  Candidatures
                </span>
                <span className="text-white font-bold text-base sm:text-lg md:text-xl drop-shadow-md">
                  {submissionCount} / 1000
                </span>
              </div>
              <div className="w-full bg-white/25 rounded-full h-3 sm:h-4 overflow-hidden shadow-inner">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="h-full bg-gradient-to-r from-accent to-yellow-400 rounded-full relative shadow-lg"
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                </motion.div>
              </div>
              <p className="text-white/90 text-xs sm:text-sm mt-2 text-center drop-shadow-md">
                {1000 - submissionCount} places restantes
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-12 px-2 max-w-2xl mx-auto"
          >
            <button
              onClick={handleParticipateClick}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-accent text-white font-bold rounded-full hover:shadow-2xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-[0_8px_30px_rgba(255,107,53,0.4),inset_2px_2px_2px_0px_rgba(255,255,255,.5),7px_7px_20px_0px_rgba(0,0,0,.1),4px_4px_5px_0px_rgba(0,0,0,.1)] hover:shadow-[0_12px_40px_rgba(255,107,53,0.6),4px_4px_6px_0_rgba(255,255,255,.5),-4px_-4px_6px_0_rgba(116,125,136,.5),inset_-4px_-4px_6px_0_rgba(255,255,255,.2),inset_4px_4px_6px_0_rgba(0,0,0,.4)] hover:brightness-110"
            >
              Candidater maintenant
              <i className="ri-arrow-right-line text-base sm:text-lg md:text-xl"></i>
            </button>
            <Link
              to="/comment-ca-marche"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-white/15 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/30 hover:bg-white/25 hover:border-white/40 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-lg hover:shadow-xl"
            >
              Comment ça marche
              <i className="ri-play-circle-line text-base sm:text-lg md:text-xl"></i>
            </Link>
            <button
              onClick={() => navigate('/chat-vocal')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-white/15 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/30 hover:bg-white/25 hover:border-white/40 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-lg hover:shadow-xl"
            >
              Chat vocal
              <i className="ri-mic-line text-base sm:text-lg md:text-xl"></i>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Pourquoi Tulaura existe Section - Déplacée après le hero */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white px-4 sm:px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-accent/10 rounded-full mb-3 sm:mb-4">
              <i className="ri-lightbulb-line text-accent text-sm sm:text-base"></i>
              <span className="text-accent text-xs sm:text-sm font-semibold">Notre Mission</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 px-2">
              Pourquoi Tulaura existe
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100">
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                Trop d'entrepreneurs talentueux abandonnent faute d'accès au financement.
              </p>
              
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-red-500 to-red-600 rounded-lg mb-3">
                    <i className="ri-bank-line text-white text-lg"></i>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                    Les banques demandent des garanties impossibles
                  </p>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg mb-3">
                    <i className="ri-line-chart-line text-white text-lg"></i>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                    Les investisseurs exigent une traction déjà établie
                  </p>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg mb-3">
                    <i className="ri-trophy-line text-white text-lg"></i>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-700 leading-snug">
                    Les concours traditionnels favorisent les réseaux établis
                  </p>
                </div>
              </div>

              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                Le problème n'est pas le manque de projets viables.<br />
                <strong>C'est le manque d'opportunités équitables.</strong>
              </p>

              <div className="bg-gradient-to-r from-secondary/10 to-primary/10 rounded-xl p-6 mb-6">
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-secondary text-center">
                  Tulaura change les règles du jeu.
                </p>
              </div>

              <div className="text-center space-y-4">
                <p className="text-sm sm:text-base md:text-lg text-gray-700">
                  <strong>1000 places. Un formulaire. Résultats à la fin du compte à rebours.</strong><br />
                  Mentorat, feedback et communauté garantis pour tous.
                </p>

                <p className="text-lg sm:text-xl md:text-2xl font-semibold text-accent">
                  Votre projet mérite une chance équitable.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* À qui s'adresse le concours Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white px-4 sm:px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4"
            >
              <i className="ri-team-line text-primary text-sm sm:text-base"></i>
              <span className="text-primary text-xs sm:text-sm font-semibold">Public Cible</span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 px-2">
              À qui s'adresse le concours
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Tulaura est ouvert à tous les porteurs de projets ambitieux
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {[
              {
                icon: 'ri-seedling-line',
                title: 'Entrepreneurs débutants',
                description: 'Vous avez une idée et la motivation pour la concrétiser',
                color: 'from-green-500 to-emerald-600',
              },
              {
                icon: 'ri-lightbulb-flash-line',
                title: 'Porteurs de projets innovants',
                description: 'Votre projet apporte une solution nouvelle à un problème existant',
                color: 'from-yellow-500 to-orange-600',
              },
              {
                icon: 'ri-rocket-line',
                title: 'Startups early-stage',
                description: 'Vous avez déjà lancé votre projet et cherchez à accélérer',
                color: 'from-cyan-500 to-blue-600',
              },
              {
                icon: 'ri-briefcase-line',
                title: 'Freelances / Indépendants',
                description: 'Vous souhaitez développer votre activité ou lancer un nouveau service',
                color: 'from-secondary to-primary',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
              >
                <div className={`w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-2xl mb-4 mx-auto`}>
                  <i className={`${item.icon} text-xl sm:text-2xl text-white`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 text-center">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed text-center">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 sm:mt-12 text-center"
          >
            <div className="bg-gradient-to-r from-accent/10 to-accent/5 rounded-2xl p-6 sm:p-8 border border-accent/20 max-w-3xl mx-auto">
              <p className="text-sm sm:text-base md:text-lg text-gray-800 font-semibold">
                <i className="ri-information-line text-accent mr-2"></i>
                Quel que soit votre profil, si vous avez un projet entrepreneurial viable et la détermination de le mener à bien, Tulaura est fait pour vous.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Prix Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white px-4 sm:px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-secondary/10 rounded-full mb-3 sm:mb-4"
            >
              <i className="ri-gift-line text-secondary text-sm sm:text-base"></i>
              <span className="text-secondary text-xs sm:text-sm font-semibold">Dotations</span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 px-2">
              16 000€ de dotations
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Trois lauréats récompensés
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {[
              {
                rank: '1er',
                prize: '10 000€',
                color: 'from-yellow-400 to-yellow-600',
                icon: 'ri-trophy-line',
                benefits: ['Mentorat individuel 1h', 'Mise en relation investisseurs', 'Visibilité médias'],
              },
              {
                rank: '2ème',
                prize: '5 000€',
                color: 'from-gray-300 to-gray-500',
                icon: 'ri-medal-line',
                benefits: ['Coaching stratégique 1h', 'Réseau partenaires', 'Support communication'],
              },
              {
                rank: '3ème',
                prize: '1 000€',
                color: 'from-orange-400 to-orange-600',
                icon: 'ri-award-line',
                benefits: ['Appel conseil 30min', 'Outils business', 'Accès communauté premium'],
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl p-5 sm:p-6 md:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 ${
                  index === 0 ? 'border-yellow-200 lg:scale-105' : 'border-gray-100'
                }`}
              >
                <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-2xl mb-4 sm:mb-6 mx-auto`}>
                  <i className={`${item.icon} text-xl sm:text-2xl md:text-3xl text-white`}></i>
                </div>
                <div className="text-center mb-4 sm:mb-6">
                  <div className="text-sm sm:text-base md:text-lg font-semibold text-gray-600 mb-1 sm:mb-2">{item.rank} Prix</div>
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">{item.prize}</div>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {item.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-secondary mt-0.5 flex-shrink-0 text-sm sm:text-base"></i>
                      <span className="leading-snug">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Processus Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gray-50 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4"
            >
              <i className="ri-route-line text-primary text-sm sm:text-base"></i>
              <span className="text-primary text-xs sm:text-sm font-semibold">Processus</span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 px-2">
              Comment participer
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Processus en 4 étapes
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {[
              {
                step: '01',
                title: 'Inscription',
                description: 'Créez votre compte et réglez les frais de participation',
                icon: 'ri-user-add-line',
                color: 'from-accent to-accent-dark',
              },
              {
                step: '02',
                title: 'Dépôt du dossier',
                description: 'Soumettez votre projet au format PDF',
                icon: 'ri-upload-cloud-line',
                color: 'from-secondary to-primary',
              },
              {
                step: '03',
                title: 'Évaluation',
                description: 'Analyse par un jury d\'experts indépendants',
                icon: 'ri-search-eye-line',
                color: 'from-cyan-500 to-blue-600',
              },
              {
                step: '04',
                title: 'Résultats',
                description: 'Annonce des lauréats et versement des dotations',
                icon: 'ri-trophy-line',
                color: 'from-yellow-400 to-orange-500',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-4 sm:p-5 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-xl sm:rounded-2xl mb-3 sm:mb-4`}>
                    <i className={`${item.icon} text-lg sm:text-xl md:text-2xl text-white`}></i>
                  </div>
                  <div className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-br ${item.color} bg-clip-text text-transparent mb-2 sm:mb-3`}>
                    {item.step}
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/comment-ca-marche"
              className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all cursor-pointer text-sm sm:text-base"
            >
              Voir le règlement complet
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Programme Mentora Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-secondary/10 via-primary/10 to-accent/10 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Ce que vous recevez
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-700 font-semibold mb-2">
                Inclus dans votre participation
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-600">
                Garantis pour tous les participants :
              </p>
            </motion.div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10">
            {[
              {
                icon: 'ri-telegram-line',
                title: 'Communauté Telegram privée',
                description: 'Accès à un groupe d\'entrepreneurs actifs : échanges quotidiens, ressources exclusives, formations et conseils d\'experts',
              },
              {
                icon: 'ri-bar-chart-line',
                title: 'Feedback structuré',
                description: 'Analyse écrite détaillée de votre projet par le jury avec axes d\'amélioration concrets',
              },
              {
                icon: 'ri-book-open-line',
                title: 'Ressources business',
                description: 'Templates, guides pratiques et outils pour structurer et développer votre projet',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-secondary to-primary rounded-xl mb-3">
                  <i className={`${item.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-5 sm:p-6 md:p-8 border-2 border-yellow-200 shadow-lg mb-8 sm:mb-10"
          >
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">
              Les 3 lauréats reçoivent en plus :
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[
                { icon: 'ri-trophy-line', text: 'Dotation en cash (10k€, 5k€, 1k€)' },
                { icon: 'ri-phone-line', text: 'Session de mentorat individuel' },
                { icon: 'ri-megaphone-line', text: 'Visibilité médiatique' },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-white text-sm sm:text-base`}></i>
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-800">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-accent to-accent-dark rounded-2xl p-5 sm:p-6 md:p-8 text-center shadow-xl"
          >
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white leading-tight">
              Les 100€ de participation sont un engagement,<br className="hidden sm:block" />
              <span className="text-yellow-300">pas un pari</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-primary via-secondary to-secondary-light px-4 sm:px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 md:mb-6 px-2 leading-tight drop-shadow-lg">
              Première édition :<br />
              <span className="text-accent drop-shadow-[0_2px_8px_rgba(255,107,53,0.8)]">Vous écrivez l'histoire</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/95 mb-6 sm:mb-8 md:mb-10 leading-relaxed px-2 drop-shadow-md">
              1000 places disponibles. Résultats annoncés 15 jours après la clôture.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-6 px-2">
              <button
                onClick={handleParticipateClick}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-accent text-white font-bold rounded-full hover:shadow-2xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-[0_8px_30px_rgba(255,107,53,0.5),inset_2px_2px_2px_0px_rgba(255,255,255,.5),7px_7px_20px_0px_rgba(0,0,0,.1),4px_4px_5px_0px_rgba(0,0,0,.1)] hover:shadow-[0_12px_40px_rgba(255,107,53,0.7),4px_4px_6px_0_rgba(255,255,255,.5),-4px_-4px_6px_0_rgba(116,125,136,.5),inset_-4px_-4px_6px_0_rgba(255,255,255,.2),inset_4px_4px_6px_0_rgba(0,0,0,.4)] hover:brightness-110"
              >
                Rejoindre la première édition
                <i className="ri-arrow-right-line text-base sm:text-lg md:text-xl"></i>
              </button>
              <Link
                to="/reglement"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-white/15 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/30 hover:bg-white/25 hover:border-white/40 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-lg hover:shadow-xl"
              >
                Consulter le règlement
                <i className="ri-file-list-line text-base sm:text-lg md:text-xl"></i>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Projects Carousel */}
      <ProjectsCarousel />

      {/* FAQ Section */}
      <section id="faq" className="py-12 sm:py-16 md:py-24 bg-white px-4 sm:px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-accent/10 rounded-full mb-3 sm:mb-4"
            >
              <i className="ri-question-line text-accent text-sm sm:text-base"></i>
              <span className="text-accent text-xs sm:text-sm font-semibold">FAQ</span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 px-2">
              Questions fréquentes
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4">
              Réponses claires et directes
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {[
              {
                question: 'Qui peut participer ?',
                answer: 'Toute personne majeure portant un projet entrepreneurial. Aucun prérequis de diplôme, statut juridique ou secteur d\'activité.',
              },
              {
                question: 'Quel format pour le dossier ?',
                answer: 'PDF uniquement, 10 Mo maximum. Contenu obligatoire : résumé exécutif, description du projet, analyse de marché, modèle économique, équipe, projections financières et plan de développement.',
              },
              {
                question: 'Comment sont évalués les projets ?',
                answer: 'Jury d\'experts indépendants. Grille d\'évaluation sur 100 points : innovation (25), viabilité économique (25), impact (20), présentation (15), faisabilité (15).',
              },
              {
                question: 'Quand les résultats sont-ils annoncés ?',
                answer: 'Les résultats seront publiés 15 jours après la clôture des inscriptions. Les lauréats seront contactés directement par email et téléphone.',
              },
              {
                question: 'Comment sont versées les dotations ?',
                answer: 'Virement bancaire sous 7 jours après l\'annonce. Aucune condition d\'utilisation des fonds.',
              },
              {
                question: 'Est-ce la première édition ?',
                answer: 'Oui. Première édition de Tulaura. Pas de résultats antérieurs, pas de témoignages inventés. Un concours transparent avec des règles claires et des engagements tenus.',
              },
            ].map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-50 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all group"
              >
                <summary className="flex items-center justify-between p-4 sm:p-5 md:p-6 cursor-pointer list-none">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 pr-3 sm:pr-4 leading-snug">{faq.question}</h3>
                  <i className="ri-arrow-down-s-line text-xl sm:text-2xl text-secondary group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6">
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
      
      {/* Modal de connexion */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-secondary to-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-line text-3xl text-white"></i>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Connexion requise
              </h2>
              <p className="text-gray-600 text-sm">
                Vous devez être connecté pour participer au concours
              </p>
            </div>

            <div className="space-y-3">
              <Link
                to="/inscription"
                onClick={() => setShowLoginModal(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary to-primary text-white font-bold rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-user-add-line"></i>
                Créer un compte
              </Link>
              
              <Link
                to="/connexion"
                onClick={() => setShowLoginModal(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-login-box-line"></i>
                Se connecter
              </Link>
            </div>

            <button
              onClick={() => setShowLoginModal(false)}
              className="mt-4 w-full text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors cursor-pointer"
            >
              Annuler
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
